import { onSnapshot, Query, QuerySnapshot } from "firebase/firestore";
import { useEffect, useState } from "react";
import { Post } from "../models/Post";
import { postsListStatus } from "../utils/postsListStatus";

const INITIAL_BATCH = 50;

// Grow geometrically when a history contains many excluded records. Keep the
// last snapshot visible while replacing the listener with a larger window.
export function usePostsListPage(
  buildQuery: (count: number) => Query<Post> | null,
  visibleLimit: number,
) {
  const [window, setWindow] = useState({ scope: buildQuery, size: INITIAL_BATCH });
  const [result, setResult] = useState<{
    scope: typeof buildQuery;
    size: number;
    snapshot?: QuerySnapshot<Post>;
    error?: Error;
  }>();
  const fetchLimit = window.scope === buildQuery ? window.size : INITIAL_BATCH;
  const current = result?.scope === buildQuery ? result : undefined;
  const snapshot = current?.snapshot;
  const eligibleCount = snapshot?.docs.filter((doc) => postsListStatus(doc.data())).length ?? 0;
  const exhausted = Boolean(snapshot && snapshot.size < (current?.size ?? 0));
  const needsMore = Boolean(snapshot && !exhausted && eligibleCount <= visibleLimit);
  const loading = !current || current.size !== fetchLimit;

  useEffect(() => {
    const source = buildQuery(fetchLimit);
    if (!source) {
      setResult({ scope: buildQuery, size: fetchLimit });
      return;
    }
    let active = true;
    const unsubscribe = onSnapshot(source, (next) => {
      if (active) setResult({ scope: buildQuery, size: fetchLimit, snapshot: next });
    }, (error) => {
      if (active) setResult((previous) => ({
        scope: buildQuery, size: fetchLimit, error,
        snapshot: previous?.scope === buildQuery ? previous.snapshot : undefined,
      }));
    });
    return () => { active = false; unsubscribe(); };
  }, [buildQuery, fetchLimit]);

  useEffect(() => {
    if (!loading && !current?.error && needsMore) {
      setWindow({ scope: buildQuery, size: Math.max(fetchLimit * 2, visibleLimit + 1) });
    }
  }, [buildQuery, fetchLimit, visibleLimit, loading, current?.error, needsMore]);

  return {
    snapshot,
    error: current?.error,
    loading: !current?.error && (loading || needsMore),
    hasMore: eligibleCount > visibleLimit || Boolean(snapshot && !exhausted),
  };
}
